'use client';

import { motion } from 'motion/react';
import type { Transition, Variants } from 'motion/react';
import type { CSSProperties } from 'react';

import './progress-rail.css';
import { useInlineSign } from '@/i18n/direction';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * İlerleme rayı.
 *
 * Ray dört parçadan oluşuyor, soldan sağa: **dolu kısım** (siyah), onun
 * başındaki **imleç** (yeşil blok), imlecin hemen ardındaki **okuma çizgisi**
 * (yeşil, bardan yukarı taşan ince dikey) ve kalan bütün yolu kaplayan
 * **çetele** (gri dikey çizgiler). Doldurulmamış alanın kendine ait bir kutusu
 * YOK — boş yol zaten çizgilerin kendisi. Bir cetvelin okunmamış kısmı gibi:
 * ölçek hep duruyor, dolgu onu soldan yiyor.
 *
 * İki yeşil parça tek bir işaretin iki yarısı: blok gövde, ince çizgi de
 * bardan yukarı çıkan okuma ucu — nerede olduğunu barın dışından da
 * göstersin diye taşıyor. Dolgu ikisinin zemini, o yüzden siyah. Gri çizgiler
 * ne işaret ne zemin — ölçeğin kendisi.
 *
 * ── Neden böyle ───────────────────────────────────────────────────────────
 *
 * Önceki çizimde dolu kısmın ardında gri bir kutu, sonra limit çizgisi, en
 * sonda da limitin ÖTESİNDE sabit on çetele vardı. İki kusuru vardı:
 *
 *   1. Gri kutu ile çetele aynı şeyi (boş yol) iki farklı dille anlatıyordu.
 *   2. Çetele sayısı sabitti — yani "kalan yol" değil, süs. Doluluk değişince
 *      çizgiler aynı kalıyordu.
 *
 * Şimdi tek bir dil var: çizgi = gidilmemiş yol. Yeşil çizgi eski sırasını
 * koruyor — çetele hâlâ ONDAN SONRA geliyor — ama artık sabit bir limiti
 * değil doluluğu işaretliyor, yani imleçle birlikte ray boyunca ilerliyor.
 *
 * ── Ölçüler tek bir sayıya bağlı ──────────────────────────────────────────
 *
 * Çizim, bar yüksekliği 60px olan bir ızgaraya oturuyor. Aşağıdaki her değer o
 * yüksekliğin katı; CSS'te de öyle duruyor (`./progress-rail.css` — bileşen
 * kendisi import ediyor, sayfaya bağımlı değil).
 * Tek `--prail-h` değişince oranlar bozulmadan her şey ölçekleniyor — 390px'te
 * de 2560px'te de aynı çizim.
 *
 *     bar yüksekliği     60      çetele adımı        8
 *     ray genişliği    461.7     çetele kalınlığı  1.5
 *     boşluk            1.5      çetele boyu        40
 *     radius              3      imleç kalınlığı   14.2
 *     okuma çizgisi     1.5      çizgi taşması      11
 *
 * Sayılar gözle değil ölçümle çıkarıldı. Kaynak görüntüler büyütülerek
 * kaydedilmiş — kenarlar bulanık ve yeniden örnekleme halkalanması var, yani
 * tek bir eşikle ölçmek yanlış sayı veriyor. Ölçüler bu yüzden "mürekkep
 * integrali" ile alındı: bulanıklık toplam koyuluğu korur, yalnızca tepe
 * değerini düşürür. Bir öğenin genişliği = koyuluk toplamı ÷ gerçek renk
 * derinliği, konumu = o toplamın ağırlık merkezi.
 *
 * 🔑 İmleç kalınlığı rayın GENİŞLİĞİNE değil YÜKSEKLİĞİNE göre okunuyor
 * (0.2367 × yükseklik). Kaynak görüntüde ray bizimkinden çok daha basıktı;
 * genişliğe oranlansaydı ölçek taşınamazdı. Yükseklik ise ızgaranın birimi —
 * imleç orada 15.2px, bar 64.5px, oran 0.236.
 *
 * Renkler ölçümden: yeşil #76f98d (rgb 118 249 141) — imleç ve okuma çizgisi;
 * dolgu #000; çetele #aaa.
 *
 * ── Çetele sayısı hesaplanıyor, verilmiyor ────────────────────────────────
 *
 * Çeteleler rayın SAĞ kenarına yaslı ve merkez adımı sabit (8): ızgara rayın
 * kendisine ait, doluluğa değil. Doluluk arttıkça imleç soldaki çizgileri tek
 * tek yutuyor; kalanlar yerinden kıpırdamıyor. Sola yaslansaydı — yani ilk
 * çizgi imlecin hemen ardından başlasaydı — bu kez sağ uçta değişken bir
 * boşluk kalırdı ve rayın sonu (yüzde yüz) belirsizleşirdi.
 *
 * Kaç çizgi sığdığı ölçüm gerektirmiyor: ray da çeteleler de aynı 60'lık
 * ızgaranın katları olduğu için oran ölçekten bağımsız. Hesap bu yüzden
 * tasarım biriminde yapılıyor (`tickCount`), piksel okumadan.
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Ray soldan sağa çiziliyor: önce dolgu, sonra imleç, sonra okuma çizgisi, en
 * son çetele. Okuma yönü ile çizim yönü aynı — hareket bir sıra anlatıyor,
 * süs değil.
 *
 * Dolgu `x: -100% → 0` ile, kırpan kabın içinde kayıyor. Bunun `scaleX`e
 * tercih edilme sebebi köşeler: 3px'lik yarıçap scaleX altında yamuluyor,
 * kayan dolgu ise kabın kendi köşesini kullanıyor.
 *
 * İmleç, okuma çizgisi ve çeteleler tabandan yukarı `scaleY` ile açılıyor —
 * üçü aynı jest, çünkü üçü aynı ölçeğin parçası. Yay `snap`: ölçü çizgisi
 * kararlı oturmalı.
 *
 * Çetele dalgası SÜREYE bağlı, çizgi sayısına değil: 55 çizgiyi 0.04'lük
 * sabit gecikmeyle sıralamak 2.2 saniye sürerdi. Adım `RULER_WAVE / sayı` ile
 * sıkışıyor, yani dalga ray dolu da olsa boş da olsa aynı sürede geçiyor —
 * hızlanan bir süpürme, uzayan bir kuyruk değil.
 *
 * Azaltılmış hareket kök layout'taki `<MotionConfig reducedMotion="user">`
 * ile karşılanıyor: dönüşümler anında hedefe atlıyor, dolgunun opaklığı
 * açılmaya devam ediyor. Burada döngü ya da parallax yok, elle kontrol
 * gerekmiyor.
 */

/* ── Tasarım birimi: bar yüksekliği = 60 ─────────────────────────────────── */

/** Rayın toplam genişliği. CSS'teki oranla aynı sayı (7.695 × h). */
const RAIL = 461.7;
/** Parçalar arası boşluk — `--prail-gap`. */
const GAP = 1.5;
/** İmleç kalınlığı — `--prail-cursor-w`. */
const CURSOR = 14.2;
/** Okuma çizgisinin kalınlığı — `--prail-needle-w`, çeteleyle aynı. */
const NEEDLE = 1.5;
/** İki komşu çetele MERKEZİ arası mesafe — `--prail-step`. */
const STEP = 8;
/** Çetele kalınlığı — `--prail-tick-w`. */
const TICK = 1.5;
/** Okuma çizgisinin komşularına en az mesafesi. Altına inince bir çetele
    eksiltiliyor — iki yeşil parça birbirine yapışmasın diye. */
const MIN_CLEAR = 3;

/** Çetele dalgasının toplam süresi. Çizgi sayısından bağımsız. */
const RULER_WAVE = dur.slow;

/**
 * Çetele sayısı ve okuma çizgisinin kayması.
 *
 * Ray bir flex satırı: n dolu bölme + imleç + okuma çizgisi + çetele kabı,
 * aralarında n+2 boşluk. Boşluklar, imleç ve çizgi sabit genişlikte olduğu
 * için paylaşılan alan `RAIL − (n+2)·GAP − CURSOR − NEEDLE`; çetele kabı
 * bunun `rest` kadarını alıyor.
 *
 * k çizgi `(k−1)·STEP + TICK` yer kaplıyor (merkez adımı sabit, en sondaki
 * çizginin ardında boşluk yok). Buradan k çözülüyor.
 *
 * Sığdıktan sonra artan pay (0…1 adım) `nudge` ile ikiye bölünüyor: çizgi
 * kendi boşluğunun içinde yarısı kadar sağa kayıyor, böylece yeşil bloğa ve
 * ilk çeteleye mesafesi EŞİT oluyor. Kayma CSS'te negatif sağ boşlukla
 * dengeleniyor — ray genişliği değişmiyor, yalnızca çizgi yer değiştiriyor.
 *
 * 🔑 Artık pay çok küçükse (mesafe `MIN_CLEAR`in altına düşüyorsa) bir çetele
 * ELENİYOR ve pay bir tam adım büyüyor. Çeteleler sağ kenara yaslı ve adımı
 * sabit olduğu için ilk çizginin yeri adımın katı kadar oynayabiliyor; ara bir
 * değer seçmek mümkün değil. Elemenin tek işi iki yeşil parçanın birbirine
 * yapışmasını önlemek — mesafe bu yüzden dar bir bantta kalıyor, sabit değil.
 */
function rulerPlan(segmentCount: number, rest: number) {
  const room = (RAIL - (segmentCount + 2) * GAP - CURSOR - NEEDLE) * rest;
  const spare = (k: number) => room - ((k - 1) * STEP + TICK);

  const fit = room < TICK ? 0 : Math.floor((room - TICK) / STEP) + 1;
  const ticks = fit > 0 && GAP + spare(fit) / 2 < MIN_CLEAR ? fit - 1 : fit;

  return ticks > 0 ? { ticks, nudge: spare(ticks) / 2 } : { ticks: 0, nudge: 0 };
}

/**
 * Dolu bölmenin rengi. Yeşil, işaretin iki parçasına ait: imleç ve okuma
 * çizgisi. Dolgu onların zemini olduğu için siyah — yeşil ancak koyu bir
 * gövdenin ucunda okunuyor. Yığılmış okuma yapan çağıran (harcanan + bekleyen gibi)
 * ikinci bölmenin tonunu kendi vermeli; uydurulmuş bir gri anlam taşımazdı.
 */
const TONE = '#000';

const rail: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: stagger.tight } },
};

/** Kırpan kap: kendi opaklığını açar, dolguya varyantı aktarır. */
const segment: Variants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { duration: dur.fast, ease: ease.out } },
};

/** Dolgu satırın BAŞINDAN içeri kayar (sağdan sola dilde sağdan — ray `flex`
    olduğu için bölmeler zaten aynalanıyor). Uzun ray için yumuşak yay —
    büyük yüzey. */
const sweep = (s: Transition, sign: 1 | -1): Variants => ({
  hidden: { x: `${-100 * sign}%` },
  show: { x: '0%', transition: s },
});

/** İmleç, okuma çizgisi ve çetele: tabandan yukarı. */
const rise: Variants = {
  hidden: { opacity: 0, scaleY: 0 },
  show: { opacity: 1, scaleY: 1, transition: springFade(spring.snap) },
};

/** Dalga adımı çizgi sayısına göre sıkışıyor; toplam süre sabit kalıyor. */
const ruler: Variants = {
  hidden: {},
  show: (count: number) => ({
    transition: { staggerChildren: Math.min(stagger.tight, RULER_WAVE / Math.max(count, 1)) },
  }),
};

export type RailSegment = {
  /** Rayın 0–1 arası payı. Dilimlerin toplamı 1'i aşmamalı. */
  value: number;
  /** Verilmezse siyah. Yığılmış okumada ikinci ve sonrası için şart. */
  color?: string;
};

export type ProgressRailProps = {
  /**
   * Dolu bölmeler, soldan sağa. Tek değerli bir çubuk için tek elemanlı dizi
   * yeter; yığılmış okuma için birden fazla verilir (örn. harcanan + bekleyen).
   * Değerler burada SÜZÜLÜYOR (`clampSegments`): NaN/sonsuz → 0, her dilim
   * 0–1, toplam 1'i aşarsa sondan kırpılır. Aşımı (`1.629 / 25`) ray
   * anlatamaz — çağıran onu METİNLE söyler, ray %100'de durur.
   */
  segments: RailSegment[];
  /** Ray bir çizim; ekran okuyucuya ne anlattığı dışarıdan veriliyor. */
  label: string;
  /**
   * ÖLÇÜ KABININ sınıfı (`.prail-fit`). Tavan buradan verilir:
   * `.benim-ray { --prail-h-max: 30px; }` (7.5'in katı — CSS başlığı).
   */
  className?: string;
};

/** Dilimleri rayın anlayacağı aralığa indirir — uç değer düzeni bozmasın. */
export function clampSegments(segments: readonly RailSegment[]): RailSegment[] {
  let left = 1;
  return segments.map((segment) => {
    const raw = Number.isFinite(segment.value) ? segment.value : 0;
    const value = Math.min(Math.max(0, raw), left);
    left -= value;
    return { ...segment, value };
  });
}

export function ProgressRail({ segments: input, label, className }: ProgressRailProps) {
  const sign = useInlineSign();
  const segments = clampSegments(input);
  /* Kalan yol. Dilimler süzüldüğü için toplam en fazla 1 — sıfırın altına
     inmiyor. */
  const rest = Math.max(0, 1 - segments.reduce((s, x) => s + x.value, 0));
  const { ticks, nudge } = rulerPlan(segments.length, rest);

  /* 🔑 Ölçü kabı bileşenin İÇİNDE (1 Eki 2026). `--prail-h` kabın genişliğinden
     (`100cqw`) türüyor; kabı çağırana bırakmak, unutulduğunda en yakın ATA
     kabı ölçmek demekti — sohbet kartında ray 461px genişlikle sütunundan
     taştı. Dışarıda ayrıca `.prail-fit` saran eski çağıranlar zararsız: iç
     kap dış kabın genişliğini alıyor, tavan değişkeni miras geçiyor. */
  return (
    <div className={className ? `prail-fit ${className}` : 'prail-fit'}>
      <motion.div
        role="img"
        aria-label={label}
        className="prail"
        variants={rail}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.5 }}
      >
        {/* Bölmeler rayı `flex` payıyla bölüşüyor. Yüzde genişlik verilseydi
            aradaki boşluklar ve imleç hesaba katılmaz, bölmeler o kadar
            taşardı. */}
        {segments.map((s, i) => (
          <motion.div
            key={i}
            className="prail-seg"
            style={{ flex: `${s.value} 0 0` }}
            variants={segment}
          >
            <motion.div
              className="prail-fill"
              style={{ background: s.color ?? TONE }}
              variants={sweep(spring.default, sign)}
            />
          </motion.div>
        ))}

        <motion.div className="prail-cursor" variants={rise} />

        {/* Kayma tasarım biriminde geliyor (60'lık ızgara), CSS'te yüksekliğin
            katına çevriliyor — böylece her ölçekte aynı oran. */}
        <motion.div
          className="prail-needle"
          style={{ '--prail-nudge': `calc(var(--prail-h) * ${nudge / 60})` } as CSSProperties}
          variants={rise}
        />

        {/* Ray tamamen doluyken çetele kabı hiç basılmıyor: genişliği sıfır olsa
            bile flex boşluğu duruyor ve okuma çizgisinden sonra sarkan bir
            aralık kalıyordu. */}
        {ticks > 0 ? (
          <motion.div
            className="prail-ruler"
            style={{ flex: `${rest} 0 0` }}
            variants={ruler}
            custom={ticks}
          >
            {Array.from({ length: ticks }, (_, i) => (
              <motion.span key={i} className="prail-tick" variants={rise} />
            ))}
          </motion.div>
        ) : null}
      </motion.div>
    </div>
  );
}
